// Helpers for the Agent Calendar "Add Event" invitee field and the optional
// e-mailed calendar invite (.ics). Kept pure so the rules are unit-testable.
//
// The server (rialty-calendar-service) is the source of truth for what is
// actually e-mailed: invites go out only for standalone events (no
// transactionId / milestoneId / serviceRequestId) and only when
// `sendEmailInvites` is true. These helpers just stop the UI from offering or
// sending the flag where the server would ignore it.

// Build-time flag (set in the environment build file). Unset => the invitee
// field and switch stay hidden and the page behaves exactly as before.
export const EMAIL_INVITES_ENABLED = process.env.REACT_APP_CALENDAR_EMAIL_INVITES === 'true';

const EMAIL_RE = /^[^\s@<>,;:"\\]+@[^\s@<>,;:"\\]+\.[^\s@<>,;:"\\]+$/;

// "a@x.com, B@x.com ; a@x.com" -> { emails: ['a@x.com','B@x.com'], invalid: [] }.
// Splits on comma/semicolon/whitespace, drops case-insensitive duplicates
// (keeping the first spelling), and reports entries that aren't an address.
export function parseInvitees(text) {
  const emails = [];
  const invalid = [];
  const seen = new Set();
  String(text || '')
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((entry) => {
      if (!EMAIL_RE.test(entry)) {
        invalid.push(entry);
        return;
      }
      const key = entry.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      emails.push(entry);
    });
  return { emails, invalid };
}

// True when an e-mailed invite makes sense for this form state.
export function canEmailInvites({ transactionId, emails }) {
  return !transactionId && Array.isArray(emails) && emails.length > 0;
}

// Whether to send `sendEmailInvites: true`.
export function shouldSendEmailInvites({ enabled, checked, transactionId, emails }) {
  return Boolean(enabled && checked && canEmailInvites({ transactionId, emails }));
}

// Emails in `current` that aren't already guests (case-insensitive). Used when
// editing: the update endpoint doesn't take guests, so only new ones are added
// through the invite endpoint.
export function newInvitees(existing, current) {
  const have = new Set((existing || []).map((e) => String(e).toLowerCase()));
  return (current || []).filter((e) => !have.has(String(e).toLowerCase()));
}

// ── Invitee chip input helpers ───────────────────────────────────────────────
// The Add Event form keeps guests as ONE string ("a@x.com, b@y.org, par"). Every
// token followed by a separator is a committed guest (a chip); whatever trails
// the last separator is the draft being typed. Keeping the form value a plain
// string means edit-loading, validation (parseInvitees) and the save payload
// all work unchanged — and a draft the user never committed is still validated
// and sent on Save instead of being silently dropped.

// "a@x.com, b@y.org, par" -> { committed: ['a@x.com','b@y.org'], draft: 'par' }
// "a@x.com, b@y.org, "    -> { committed: ['a@x.com','b@y.org'], draft: '' }
export function splitInviteeText(value) {
  const text = String(value || '');
  const parts = text.split(/[\s,;]+/);
  const endsWithSeparator = /[\s,;]$/.test(text);
  const draft = endsWithSeparator ? '' : (parts.pop() || '');
  const committed = parts.filter(Boolean);
  return { committed, draft };
}

// Inverse of splitInviteeText: committed guests each end with a separator, so the
// draft (possibly empty) is whatever follows.
export function buildInviteeText(committed, draft = '') {
  return (committed || []).map((e) => `${e}, `).join('') + draft;
}

export function isValidEmail(text) {
  return EMAIL_RE.test(String(text || '').trim());
}

// Suggestion rows from a contacts page: skips contacts with no email, ones already added
// (case-insensitive), and the signed-in user's own address; de-dupes by email.
export function contactSuggestions(contacts, { committed = [], ownEmail = '', limit = 8 } = {}) {
  const taken = new Set(committed.map((e) => String(e).toLowerCase()));
  if (ownEmail) taken.add(String(ownEmail).toLowerCase());
  const out = [];
  (Array.isArray(contacts) ? contacts : []).forEach((c) => {
    const email = c && c.email ? String(c.email).trim() : '';
    if (!email || !isValidEmail(email)) return;
    const key = email.toLowerCase();
    if (taken.has(key)) return;
    taken.add(key);
    const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.name || '';
    out.push({ email, name });
  });
  return out.slice(0, limit);
}
